import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import type { CortexHandoffPayload } from '@/lib/notebook-cortex-handoff';

interface ChatContextType {
  isChatOpen: boolean;
  setIsChatOpen: (open: boolean) => void;
  pendingNotebookHandoff: CortexHandoffPayload | null;
  clearNotebookHandoff: () => void;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

export const useChatContext = () => {
  const context = useContext(ChatContext);
  if (context === undefined) {
    throw new Error('useChatContext must be used within a ChatProvider');
  }
  return context;
};

interface ChatProviderProps {
  children: ReactNode;
}

export const ChatProvider = ({ children }: ChatProviderProps) => {
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [pendingNotebookHandoff, setPendingNotebookHandoff] =
    useState<CortexHandoffPayload | null>(null);

  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<CortexHandoffPayload>).detail;
      if (detail?.notebookId) {
        setPendingNotebookHandoff(detail);
        setIsChatOpen(true);
      }
    };
    window.addEventListener('flowstate:cortex-handoff', handler);
    return () => window.removeEventListener('flowstate:cortex-handoff', handler);
  }, []);

  const clearNotebookHandoff = () => setPendingNotebookHandoff(null);

  return (
    <ChatContext.Provider
      value={{
        isChatOpen,
        setIsChatOpen,
        pendingNotebookHandoff,
        clearNotebookHandoff,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};
